import { useState, useEffect, useRef, useCallback } from 'react'
import Head from 'next/head'
import classnames from 'classnames'
import useClickOutside from '../../hooks/use-clickoutside'
import CTACards from '../../containers/help/cta-section'
import Magnifier from '../../components/svg/magnifier'
import { HeroSearch } from '../../components/inputs'
import PageTitle from '../../components/page-title'
import Info from '../../containers/help/info'
import FAQ from '../../containers/help/faq'
import Hero from '../../components/hero'

const QUESTIONS = [
  {
    title: 'Where is privauto available',
    name: 'availability',
    description:
      'privauto is currently available in Arkansas and Texas. Additional areas coming soon',
  },
  {
    title: 'How do I pay for the vehicle I won?',
    name: 'paying',
    description:
      'A privauto consultant will be in touch with you to instruct you how to complete your purchase',
  },
  {
    title: 'What happens when my vehicle is sold?',
    name: 'afterSold',
    description:
      'You will be instructed to bring your vehicle to the nearest concierge location where the condition and title of the vehicle will be matched to your supplied description. Once the sale is finalized, privauto will transfer the funds directly to your financial institution or provide you with a check for the value.',
  },
  {
    title: 'How much does privauto cost?',
    name: 'cost',
    description: 'There are no fees to SELL your vehicle on privauto, it’s FREE.',
  },
  {
    title: 'What happens if my vehicle doesn’t sell?',
    name: 'ifNotSold',
    description:
      'Don’t worry! privauto lets you list it as many times as you want. We also have a fixed “Buy it now” type listing!',
  },
  {
    title: 'How do you verify my identity?',
    name: 'howToVerify',
    description:
      'Our identity verification carried out by Trulioo.com, one of the most trusted names in safe and secure identity verification.',
  },
  {
    title: 'How is my personal information used?',
    name: 'howInformationUsed',
    description:
      'Your personal information is only used for the purposes of verifying your identity and we do not share or sell it to any other third parties.',
  },
]

export default function Help() {
  const searchWrapperRef = useRef()
  const accordionsRef = useRef({})
  const [searchTerm, setSearchTerm] = useState('')
  const [suggestions, setSuggestions] = useState([])
  const [showSuggestions, setShowSuggestions] = useState(true)
  const [hoveredSuggestionIndex, setHoveredSuggestionIndex] = useState(-1)
  const [openedAccordionFromSearch, setOpenedAccordionFromSearch] = useState(null)

  useEffect(() => {
    setShowSuggestions(!!searchTerm?.length)
  }, [searchTerm])

  const handleSuggestionSubmit = (name) => {
    setShowSuggestions(false)
    setOpenedAccordionFromSearch(name)
    accordionsRef.current[name].scrollIntoView({ behavior: 'smooth', block: 'center' })
  }

  useEffect(() => {
    setSuggestions(() =>
      QUESTIONS.filter(
        (quest) =>
          (quest.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
            quest.description?.toLowerCase().includes(searchTerm.toLowerCase())) &&
          searchTerm.trim().length !== 0,
      ),
    )
  }, [setSuggestions, searchTerm])

  const handleKeyDown = (event) => {
    const { key } = event

    switch (key) {
      case 'ArrowDown': {
        event.preventDefault()
        setHoveredSuggestionIndex((prev) => (prev + 1 === suggestions.length ? 0 : prev + 1))
        break
      }
      case 'ArrowUp': {
        event.preventDefault()
        setHoveredSuggestionIndex((prev) => (prev <= 0 ? suggestions.length - 1 : prev - 1))
        break
      }
      case 'Enter': {
        event.preventDefault()
        const suggestion = suggestions[hoveredSuggestionIndex]
        if (suggestion) handleSuggestionSubmit(suggestion.name)
        break
      }
      case 'Tab': {
        setShowSuggestions(false)
        break
      }
      default:
        return
    }
  }

  const handleSuggestionClick = (index) => () => {
    const suggestion = suggestions[index]
    handleSuggestionSubmit(suggestion.name)
  }

  const handleSuggestionsClickOutside = useCallback(() => {
    setShowSuggestions(false)
    setHoveredSuggestionIndex(-1)
  }, [])
  useClickOutside(searchWrapperRef, handleSuggestionsClickOutside)

  return (
    <>
      <Head>
        <title>Get Help Selling Your Car Online | Sell A Car Safely | privauto</title>
        <meta
          name='description'
          content='Need help getting started with DriversExchange.com, the best website to sell your car online safely and for the most money? privauto has you covered.'
        />
      </Head>
      <Hero desktopImageSrc='/images/heroes/help.jpg' opacity={1}>
        <div className='searchContainer'>
          <PageTitle>
            <label htmlFor='search'>
              Looking for help?
              <br />
              Search our help directory.
            </label>
          </PageTitle>
          <div className='searchWrapper' ref={searchWrapperRef}>
            <HeroSearch
              id='search'
              placeholder='Search guides, tutorials or keywords'
              aria-label='Search guides, tutorials or keywords'
              autoComplete='off'
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onFocus={() => setShowSuggestions(true)}
              onKeyDown={handleKeyDown}
              adornment={<Magnifier fill='var(--secondary-text-color)' width='17' />}
            />
            {showSuggestions && (
              <ul className='suggestions' style={{ height: `${suggestions.length * 3.8}rem` }}>
                {suggestions.map((suggestion, index) => (
                  <li
                    key={suggestion.name}
                    className={classnames({ active: index === hoveredSuggestionIndex })}
                  >
                    <button className='flatBtn' onClick={handleSuggestionClick(index)}>
                      {suggestion.title}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </Hero>

      <div className='container'>
        <section className='ctaSection'>
          <CTACards />
        </section>

        <section className='faqSection'>
          <FAQ
            accordionsRef={accordionsRef}
            openedAccordionFromSearch={openedAccordionFromSearch}
            setOpenedAccordionFromSearch={setOpenedAccordionFromSearch}
          />
        </section>

        <section className='infoSection'>
          <Info />
        </section>
      </div>

      <style jsx>{`
        .container {
          max-width: 193rem;
          width: 100%;
          margin: 0 auto;
          padding: 0 4rem;
        }

        .searchContainer {
          position: relative;
          max-width: 77.2rem;
          width: 100%;
          margin: 0 auto;
          padding: 0 1.2rem;

          :global(.inputContainer) {
            position: relative;
            max-width: 77.2rem;
            margin: 0 auto;
            font-size: 1.6rem;

            :global(svg) {
              width: 3.1rem;
              height: 3.1rem;
            }
          }

          :global(.pageTitle) {
            text-align: center;

            label {
              display: block;
              padding: 0 0 2.1rem;
              color: var(--primary-bg-color);
            }
          }

          .searchWrapper {
            position: relative;
          }

          .suggestions {
            position: absolute;
            top: 100%;
            left: 0;
            z-index: 1;
            width: 100%;
            overflow: hidden;
            background: #fff;
            border-radius: 1rem;

            li {
              button {
                width: 100%;
                height: 3.8rem;
                padding: 0 1rem;
                outline: none;
                text-align: left;
                font-size: 1.6rem;
                font-weight: 700;
              }

              &.active,
              &:hover {
                background: var(--primary-color);
                color: #fff;
              }
            }
          }
        }

        .ctaSection {
          position: relative;
          bottom: 5.5rem;
        }

        .faqSection {
          margin: 1.9rem 0 11.5rem;
          // scroll-margin-top: 86px;
          // :global(.accordionsContainer .accordion) {
          //   scroll-margin-top: 8.6rem;
          // }
        }

        .infoSection {
          max-width: 153.2rem;
          width: 100%;
          margin: 0 auto 12rem;
        }

        @media (max-width: 1024px) {
          .searchContainer {
            :global(.inputContainer svg) {
              width: 1.5rem;
              height: 1.5rem;
            }
          }

          .container {
            max-width: 100%;
            padding: 2rem 1.1rem;
          }

          .ctaSection {
            bottom: 4.8rem;
          }

          .faqSection {
            margin: -2.3rem 0 3.7rem;
          }

          .infoSection {
            margin: 0;
          }
        }
      `}</style>
    </>
  )
}

Help.getInitialProps = async () => ({
  namespacesRequired: ['common'],
})
